import { useState, useEffect } from 'react';

import { API_BASE_URL as SHARED_API_BASE_URL } from '../utils/config';
const API_BASE = SHARED_API_BASE_URL;

export const useFormSchema = (formId, uniqueValue = null, uniqueField = null, tableName = null, extraData = {}) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sozlesmeId = extraData?.sozlesme_id || null;

  useEffect(() => {
    if (!formId) return;

    const fetchSchema = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams();
        if (uniqueValue) {
          params.append('unique_value', uniqueValue);
        }
        if (uniqueField) {
          params.append('unique_field', uniqueField);
        }
        if (tableName) {
          params.append('table_name', tableName);
        }
        if (sozlesmeId) {
          params.append('sozlesme_id', sozlesmeId);
        }

        const url = `${API_BASE}/form/formlar/${formId}/schema?${params.toString()}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.detail || errorMessage;
            console.error('Backend hata detayı:', errorData);
          } catch (e) {
            const errorText = await response.text();
            console.error('Backend hata metni:', errorText);
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        setSchema(data);
      } catch (err) {
        console.error('Error fetching form schema:', err);
        const errorMessage = err.message || 'Form şeması yüklenirken hata oluştu';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [formId, uniqueValue, uniqueField, tableName, sozlesmeId]);
  return { schema, loading, error };
};

export const useFormSubmission = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitForm = async (formData, schema, uniqueField = null, uniqueValue = null, extraData = {}, userId = null) => {
    setSubmitting(true);
    setError(null);

    try {
      const finalFunction = extraData?._override_function || schema.function;
      const cleanExtraData = extraData ? { ...extraData } : {};
      delete cleanExtraData._override_function;
      const maptriks_name = extraData?.maptriks_name;
      const folder = extraData?.folder;
      const payload = {
        form_id: schema.formId,
        table_name: schema.tableName,
        function: finalFunction,  
        form_data: formData,
        unique_field: uniqueField,
        unique_value: uniqueValue,
        extra_data: cleanExtraData,
        user_id: userId,
        maptriks_name: maptriks_name,
        folder: folder
      };
      const response = await fetch(`${API_BASE}/form/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (err) {
      console.error('Form Submit Hatası:', err);
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };
  return { submitForm, submitting, error };
};

export const useIlListesi = () => {
  const [ilListesi, setIlListesi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIlListesi = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE}/form/il-listesi`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setIlListesi(data.data);
        } else {
          throw new Error('API returned error');
        }
      } catch (err) {
        console.error('Error fetching il listesi:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIlListesi();
  }, []);

  return { ilListesi, loading, error };
};

export const useIlceListesi = (ilNo = null) => {
  const [ilceListesi, setIlceListesi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ilNo) {
      setIlceListesi([]);
      return;
    }

    const fetchIlceListesi = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE}/form/ilce-by-il/${ilNo}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setIlceListesi(data.data);
        } else {
          throw new Error('API returned error');
        }
      } catch (err) {
        console.error('Error fetching ilce listesi:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIlceListesi();
  }, [ilNo]);

  return { ilceListesi, loading, error };
};

export const useTalepListesi = () => {
  const [talepListesi, setTalepListesi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTalepListesi = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE}/form/talep-listesi`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setTalepListesi(data.data);
        } else {
          throw new Error('API returned error');
        }
      } catch (err) {
        console.error('Error fetching talep listesi:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTalepListesi();
  }, []);

  return { talepListesi, loading, error };
};